import { messages } from "@/config/messages";
import { EGender, EStatus } from "@/Core/enums";
import { IEntityWithSoftDeleteDto } from "@Interfaces/IEntities";
import { z } from "zod";
import { CityResponseDto } from "./city-dto";
import { CountryResponseDto } from "./country-dto";
import { EntityResponseDto } from "./entity-dto";

export interface UserResponseDto extends IEntityWithSoftDeleteDto {
    name?: string;
    email?: string;
    phoneNumber?: string;
    picture?: string;
    pictureIsLink?: boolean;
    sex?: EGender;
    status: EStatus;
    isDisabled?: boolean;
    resetPassword?: boolean;
    verifiedEmail?: boolean;
    verification?: string;
    refreshToken?: string;
    refreshTokenExpiryTime?: Date;
    //
    isSMS?: boolean;
    isWhatsApp?: boolean;
    isEmail?: boolean;
    //
    twoFA?: boolean;
    base32Secret?: string;
    isReviewer?: boolean;
    provider?: string;
    source?: string;

    permissions?: string[];
    entityId?: string;
    entity?: EntityResponseDto;

    roles?: string[];
    rolesId?: string[];
    // roles?: UserRoleResponseDto[];

    countryId?: string;
    country?: CountryResponseDto;
    city?: CityResponseDto;
    cityId?: string;
}

// AssignPermissionsToEmployeeDto
export interface UserAssignPerDto {
    employeeId?: string;
    permissions?: string[];
}

// zod schema for EmployeeFormDto
export const userFormDtoSchema = z
    .object({
        id: z.string().optional(),
        name: z
            .string({
                required_error: "Name field is required",
            })
            .min(1, {
                message: "Name field is required",
            }),
        email: z
            .string({
                required_error: "Email field is required",
            })
            .email({
                message: "Invalid email address",
            }),
        phoneNumber: z
            .string({
                required_error: "PhoneNumber field is required",
            })
            .max(12, {
                message: "PhoneNumber field must be less than 12 characters",
            }),
        sex: z
            .string({
                required_error: "Sex field is required",
            })
            .min(1, {
                message: "Sex field is required",
            }),

        picture: z.instanceof(File).optional(),
        rolesId: z
            .array(
                z.string({
                    required_error: "Role field is required",
                }),
            )
            .default([]),
        entityId: z
            .string({
                required_error: "Entity field is required",
            })
            .min(1, {
                message: "Entity field is required",
            }),

        countryId: z.string({
            required_error: "Country field is required",
        }),
        cityId: z.string({
            required_error: "City field is required",
        }),
        password: z
            .string()
            .min(1, { message: messages.passwordRequired })
            .min(6, { message: messages.passwordLengthMin })
            .optional(),
        confirmPassword: z
            .string()
            .min(1, { message: messages.confirmPasswordRequired })
            .min(6, { message: messages.passwordLengthMin })
            .optional(),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: messages.passwordsDidNotMatch,
        path: ["confirmPassword"], // Correct path for the confirmedPassword field
    });

export type UserFormDto = z.infer<typeof userFormDtoSchema>;

// zod schema

// EmployeeAddDto Schema
export const UserIsManagerSchema = z.object({
    isManager: z.boolean().default(false),
});

export type UserIsManagerDto = z.infer<typeof UserIsManagerSchema>;

// EmployeeAddDto Schema
export const UserResetPasswordSchema = z
    .object({
        id: z.string().min(1, { message: "field is required" }),
        // password: z
        //     .string()
        //     .trim()
        //     .min(6, { message: "password must be at least 6 characters" }),

        password: z
            .string()
            .min(1, { message: messages.passwordRequired })
            .min(6, { message: messages.passwordLengthMin }),
        confirmPassword: z
            .string()
            .min(1, { message: messages.confirmPasswordRequired })
            .min(6, { message: messages.passwordLengthMin }),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: messages.passwordsDidNotMatch,
        path: ["confirmPassword"], // Correct path for the confirmedPassword field
    });

export type UserResetPasswordDto = z.infer<typeof UserResetPasswordSchema>;

// assign role schema
export const UserAssignRoleSchema = z.object({
    id: z.string().min(1, { message: "field is required" }),
    remarks: z.string().min(1, { message: "Remarks is required" }),
    rolesId: z.array(z.string().min(1, { message: "Role is required" })),
});

export type UserAssignRoleDto = z.infer<typeof UserAssignRoleSchema>;
